import React from 'react';
import { observer } from 'mobx-react';

import { Formik, Form, getIn } from 'formik';

import { FormControlLabel, MenuItem, Switch } from '@mui/material';

import { AxiosError } from 'axios';

import { Button } from 'components/Button';

import CustomModal from 'components/CustomModal';
import FieldHelpIcon from 'components/FieldHelpIcon';
import { StyledSelect, StyledTextField } from 'components/Input/styles';

import { DisplayProtectorType } from 'utils/api/types';
import { isRegisterEntityModule, ProtectorType } from 'core/constants';
import { EntityFieldType, EntityFieldRoles } from 'utils/api/modules/entities/entities.types';

import { useStore } from 'utils/hooks/store';

import { getEntityTranslation } from 'stores/EntityDetails/utils';

import { colors } from 'theme/colors';

import { ButtonsWrapper, FieldHeader, FieldHeaderRow, StyledModalActions } from './styles';

import DropdownOptions from '../DropdownOptions';
import {
  EntityFieldError,
  EntityFieldTypeWithoutUUID,
  EntityFiledValidationSchema,
  getBorderStyle,
  modifyInputValue,
  possibleProtectorTypes,
} from './types';
import { SortableItemType } from '../DropdownOptions/types';

import StagesOptions from '../StagesOptions';
import { StageItemType } from '../StagesOptions/types';
import { SubmitButton, CancelButton, ErrorMessage } from './components';

// Only field whose "Don't allow future dates" toggle is exposed to users.
const NO_FUTURE_DATE_TOGGLE_FIELD_KEY = 'events__dateTime';

const CreateEditFieldModal: React.FunctionComponent<{
  entityType: string;
  editedValues: EntityFieldType;
  onClose: (refresh: boolean) => void;
}> = ({ entityType, editedValues, onClose }): React.ReactElement => {
  const {
    entityFields: { editField, createField },
    notification,
    session: { user },
  } = useStore();

  const editView = editedValues.uuid !== '';

  // @ts-expect-error
  const initialValues: EntityFieldTypeWithoutUUID = editView
    ? { ...editedValues }
    : {
        protectorType: '',
        properties: {
          lookupType: '',
        },
        name: '',
        key: '',
        internalNote: '',
        helpText: '',
      };

  const [isResetting, setIsResetting] = React.useState(false);
  const [multipleAnswers, setMultipleAnswers] = React.useState(
    editedValues.protectorType === ProtectorType.MultiChoice
  );
  const [options, setOptions] = React.useState<SortableItemType[]>(
    editedValues.properties?.enum?.map((o) => ({ ...o, displayKey: o.key, closed: o.closed })) || []
  );
  const [noFutureDate, setNoFutureDate] = React.useState(
    editedValues.properties?.roles?.includes(EntityFieldRoles.NO_FUTURE_DATE) ?? false
  );

  const updateOptions = (newOptions: SortableItemType[]): void => {
    setOptions(newOptions);
  };

  const isDropdownOption = (protectorType: string): boolean =>
    protectorType === ProtectorType.SingleChoice || protectorType === ProtectorType.MultiChoice;

  const formatEnumError = (enumError: unknown): string => {
    if (!enumError) return '';

    // Handle array case
    if (Array.isArray(enumError)) {
      return enumError.join(', ');
    }

    // Handle object case (e.g., { bmw: ["error message"] })
    if (typeof enumError === 'object' && enumError !== null) {
      const entries = Object.entries(enumError);
      return entries
        .map(([key, messages]) => {
          const messageArray = Array.isArray(messages) ? messages : [messages];
          return `${key}: ${messageArray.join(', ')}`;
        })
        .join('; ');
    }

    return '';
  };

  const prepareData = (values: EntityFieldTypeWithoutUUID): EntityFieldTypeWithoutUUID => {
    if (values.protectorType === ProtectorType.SingleChoice && multipleAnswers) {
      values.protectorType = ProtectorType.MultiChoice;
    }
    if (values.protectorType === ProtectorType.MultiChoice || values.protectorType === ProtectorType.SingleChoice) {
      // modify options for sending, key should have the value of displayKey and displayKey should be removed
      values.properties = {
        enum: options.map(({ displayKey, value, active, closed }) => ({ key: displayKey, value, active, closed })),
      };
    } else if (values.key === NO_FUTURE_DATE_TOGGLE_FIELD_KEY) {
      // always send an explicit array so turning the toggle off clears the role on the backend
      values.properties = {
        roles: noFutureDate ? [EntityFieldRoles.NO_FUTURE_DATE] : [],
      };
    }

    // if the values are not lookup type, remove the lookupType property
    if (
      values.protectorType !== ProtectorType.Lookup &&
      values.properties &&
      // @ts-ignore
      values.properties.lookupType === ''
    ) {
      delete values.properties;
    }
    // modify the key value: add a prefix with entity type
    // add prefix only if it is not already present
    if (!values.key.startsWith(`${entityType}__`)) values.key = `${entityType}__${values.key}`;

    return values;
  };

  const handleCreate = async (
    values: EntityFieldTypeWithoutUUID,
    setErrors?: (errors: EntityFieldError) => void,
    resetForm?: () => void
  ): Promise<void> => {
    try {
      const dataToSubmit = prepareData(values);
      await createField(entityType, dataToSubmit);

      if (resetForm) {
        resetForm();
      } else {
        onClose(true);
      }

      // reset options
      setOptions([]);

      notification.enqueueSuccessSnackbar('Field created successfully');
    } catch (e) {
      const error = e as AxiosError<EntityFieldTypeWithoutUUID | string[]>;

      const errorMessage = error.response?.data as EntityFieldTypeWithoutUUID | undefined;

      if (setErrors && errorMessage && !Array.isArray(errorMessage)) {
        setErrors({
          name: errorMessage.name,
          key: errorMessage.key,
          internalNote: errorMessage.internalNote || '',
          helpText: errorMessage.helpText || '',
          protectorType: errorMessage.protectorType,
          properties: {
            enum: formatEnumError(errorMessage.properties?.enum),
            lookupType: errorMessage.properties?.lookupType || '',
          },
        });
      }

      const errorData = error.response?.data;
      notification.enqueueErrorSnackbar(
        (Array.isArray(errorData) ? errorData[0] : undefined) || 'Failed to create field'
      );
    } finally {
      setIsResetting(false);
    }
  };

  const handleEdit = async (
    values: EntityFieldTypeWithoutUUID,
    editedVal: EntityFieldType,
    setErrors?: (errors: EntityFieldError) => void
  ): Promise<void> => {
    try {
      const dataToSubmit = prepareData(values);
      await editField(entityType, editedVal.uuid, dataToSubmit);
      notification.enqueueSuccessSnackbar('Field edited successfully');
      onClose(true);
    } catch (e) {
      const error = e as AxiosError<EntityFieldTypeWithoutUUID | string[]>;
      const errorMessage = error.response?.data as EntityFieldTypeWithoutUUID | undefined;

      if (setErrors && errorMessage && !Array.isArray(errorMessage)) {
        setErrors({
          name: errorMessage.name,
          key: errorMessage.key,
          internalNote: errorMessage.internalNote || '',
          helpText: errorMessage.helpText || '',
          protectorType: errorMessage.protectorType,
          properties: {
            enum: formatEnumError(errorMessage.properties?.enum),
            lookupType: errorMessage.properties?.lookupType || '',
          },
        });
      }

      const errorData = error.response?.data;
      notification.enqueueErrorSnackbar(
        (Array.isArray(errorData) ? errorData[0] : undefined) || 'Failed to edit field'
      );
    } finally {
      setIsResetting(false);
    }
  };

  const handleSubmit = async (
    values: EntityFieldTypeWithoutUUID,
    setErrors?: (errors: EntityFieldError) => void,
    resetForm?: () => void
  ): Promise<void> => {
    if (resetForm) setIsResetting(true);
    if (editView) {
      await handleEdit(values, editedValues, setErrors);
    } else {
      await handleCreate(values, setErrors, resetForm);
    }
  };

  const handleClose = (refresh: boolean, resetForm?: () => void): void => {
    if (resetForm) {
      resetForm();
    }
    onClose(refresh);
  };

  const prefix = `${entityType}__`;

  return (
    <CustomModal openModal onClose={() => handleClose(false)} modalTitle={editView ? 'Edit Field' : 'Create Field'}>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting, setErrors }) => {
          await handleSubmit(values, setErrors as (errors: EntityFieldError) => void);
          setSubmitting(false);
        }}
        validationSchema={EntityFiledValidationSchema}
      >
        {({
          values,
          handleChange,
          setFieldValue,
          isSubmitting,
          errors,
          touched,
          resetForm,
          setErrors,
          setFieldError,
        }) => {
          const isDisabled = React.useMemo(() => {
            const activeOptions = options.filter((option) => option.active);

            return (
              (activeOptions.length === 0 &&
                (values.protectorType === ProtectorType.SingleChoice ||
                  values.protectorType === ProtectorType.MultiChoice)) ||
              values.name === ''
            );
          }, [options, values.protectorType, values.name]);

          return (
            <Form>
              <FieldHeader>Field Label</FieldHeader>
              <StyledTextField
                fullWidth
                id="name"
                name="name"
                placeholder="Field Label"
                value={values.name}
                onChange={(event) => {
                  handleChange(event);
                  if (errors.name) {
                    setErrors({ ...errors, name: undefined });
                  }
                  if (editView) return;
                  const keyVal = modifyInputValue(event.target.value, prefix.length);
                  setFieldValue('key', keyVal);
                }}
                error={Boolean(errors.name && touched.name)}
                disabled={editView && !values.editable}
                helperText={errors.name}
              />
              <FieldHeaderRow>
                <FieldHeader>API Name</FieldHeader>
                <FieldHelpIcon name="API Name" helpText="Used to read from and write to the field from the API" />
              </FieldHeaderRow>
              <StyledTextField
                fullWidth
                id="key"
                name="key"
                placeholder="API Name"
                // remove the prefix from the key value
                value={values.key.replace(prefix, '')}
                onChange={(event) => {
                  handleChange(event);
                  if (errors.key) {
                    setErrors({ ...errors, key: undefined });
                  }
                }}
                disabled={editView}
                error={Boolean(errors.key && touched.key)}
                helperText={errors.key}
                InputProps={{
                  startAdornment: <p style={{ color: colors.grey[600] }}>{prefix}</p>,
                }}
              />
              <FieldHeaderRow>
                <FieldHeader>Description</FieldHeader>
                <FieldHelpIcon
                  name="Description"
                  helpText="Explains what this field represents and how its data is used."
                />
              </FieldHeaderRow>
              <StyledTextField
                fullWidth
                id="internalNote"
                name="internalNote"
                multiline
                maxRows={3}
                placeholder="Description"
                value={values.internalNote ?? ''}
                onChange={(event) => {
                  handleChange(event);
                  if (errors.internalNote) {
                    setErrors({ ...errors, internalNote: undefined });
                  }
                }}
                disabled={editView && !values.editable}
              />
              <FieldHeaderRow>
                <FieldHeader>Help text</FieldHeader>
                <FieldHelpIcon
                  name="Help text"
                  helpText="Explains how to fill in this field, shown via the info icon on the record."
                />
              </FieldHeaderRow>
              <StyledTextField
                fullWidth
                id="helpText"
                name="helpText"
                multiline
                maxRows={3}
                placeholder="Help text"
                value={values.helpText ?? ''}
                onChange={(event) => {
                  handleChange(event);
                  if (errors.helpText) {
                    setErrors({ ...errors, helpText: undefined });
                  }
                }}
                disabled={editView && !values.editable}
              />

              {/* if in edit view and type is dropdown display warning message */}
              <FieldHeader>Field Type</FieldHeader>
              <StyledSelect
                size="medium"
                displayEmpty
                fullWidth
                id="protectorType"
                name="protectorType"
                value={
                  values.protectorType === ProtectorType.MultiChoice ? ProtectorType.SingleChoice : values.protectorType
                }
                onChange={(event) => {
                  handleChange(event);
                  if (errors.protectorType) {
                    setErrors({ ...errors, protectorType: undefined });
                  }
                }}
                disabled={editView}
                error={Boolean(errors.protectorType && touched.protectorType)}
                $withoutBorder
                style={{
                  color: values.protectorType ? 'rgb(0,0,0)' : 'rgba(0, 0, 0, 0.7)',
                  border: getBorderStyle(editView, errors, touched),
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      minWidth: '552px !important',
                    },
                  },
                }}
                renderValue={(value) => {
                  if (value === '') return 'Choose an option';
                  if (values.properties && (values.properties.roles || []).includes(EntityFieldRoles.STAGE)) {
                    return 'Stage';
                  }
                  return value === ProtectorType.SingleChoice
                    ? 'Dropdown'
                    : DisplayProtectorType[value as ProtectorType];
                }}
              >
                <MenuItem value="" disabled>
                  Choose an Option
                </MenuItem>
                {possibleProtectorTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type === ProtectorType.SingleChoice ? 'Dropdown' : DisplayProtectorType[type]}
                  </MenuItem>
                ))}
              </StyledSelect>
              {Boolean(errors.protectorType && touched.protectorType) && (
                <ErrorMessage error={errors.protectorType || ''} />
              )}
              {values.key === NO_FUTURE_DATE_TOGGLE_FIELD_KEY && (
                <>
                  <FieldHeaderRow>
                    <FieldHeader>Date Restrictions</FieldHeader>
                    <FieldHelpIcon
                      name="Date Restrictions"
                      helpText="When on, users can only choose today or an earlier date. Leave it off to allow future dates."
                    />
                  </FieldHeaderRow>
                  <FormControlLabel
                    sx={{ marginRight: 0, marginLeft: 0 }}
                    control={
                      <Switch
                        checked={noFutureDate}
                        onChange={(event) => setNoFutureDate(event.target.checked)}
                        name="no-future-date"
                        size="small"
                      />
                    }
                    label={<p style={{ fontSize: '14px', margin: 0, padding: 0 }}>Don&apos;t allow future dates</p>}
                  />
                </>
              )}
              {/* display content for stages */}
              {values.properties && (values.properties.roles || []).includes(EntityFieldRoles.STAGE) && (
                <StagesOptions options={options as StageItemType[]} setOptions={updateOptions} />
              )}

              {isDropdownOption(values.protectorType) && !values.properties?.roles?.includes(EntityFieldRoles.STAGE) ? (
                <DropdownOptions
                  editView={editView}
                  options={options}
                  setOptions={updateOptions}
                  multipleAnswers={multipleAnswers}
                  setMultipleAnswers={setMultipleAnswers}
                  errors={getIn(errors, 'properties.enum') as string | undefined}
                  clearError={() => {
                    if (getIn(errors, 'properties.enum') as string | undefined) {
                      setFieldError('properties.enum', undefined);
                    }
                  }}
                />
              ) : null}

              {values.protectorType === ProtectorType.Lookup && (
                <>
                  <FieldHeader>Related to</FieldHeader>
                  <StyledSelect
                    displayEmpty
                    fullWidth
                    id="properties.lookupType"
                    name="properties.lookupType"
                    value={values.properties && values.properties.lookupType}
                    onChange={(event) => {
                      handleChange(event);
                      if (getIn(errors, 'properties.lookupType') as boolean | undefined) {
                        setFieldError('properties.lookupType', undefined);
                      }
                    }}
                    disabled={editView}
                    error={getIn(errors, 'properties.lookupType') as boolean | undefined}
                    $withoutBorder
                    style={{
                      color: values.properties ? 'rgb(0,0,0)' : 'rgba(0, 0, 0, 0.7)',
                      border: (getIn(errors, 'properties.lookupType') as boolean | undefined)
                        ? '2px solid rgb(239, 83, 80)'
                        : `1px solid ${colors.grey[400]}`,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          minWidth: '552px !important',
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Choose an Option
                    </MenuItem>
                    {user?.activeEntityModules.filter(isRegisterEntityModule).map((type) => (
                      <MenuItem key={type} value={type}>
                        {getEntityTranslation[type].singular}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  {(getIn(errors, 'properties.lookupType') as boolean | undefined) && (
                    <ErrorMessage error={getIn(errors, 'properties.lookupType') as string} />
                  )}
                </>
              )}

              {editView ? (
                <ButtonsWrapper>
                  <CancelButton handleClose={() => handleClose(false, resetForm)} editView={editView} />
                  <SubmitButton
                    disabled={isDisabled}
                    editView={editView}
                    isResetting={isResetting}
                    isSubmitting={isSubmitting}
                  />
                </ButtonsWrapper>
              ) : (
                <StyledModalActions>
                  <CancelButton handleClose={() => handleClose(false, resetForm)} editView={editView} />
                  <div>
                    <Button
                      size="large"
                      id="reset-button"
                      variant="outlined"
                      onClick={() => {
                        handleSubmit(values, undefined, resetForm);
                      }}
                      disabled={isDisabled || isSubmitting || isResetting}
                    >
                      Create and Add Another
                    </Button>
                    <SubmitButton
                      editView={editView}
                      disabled={isDisabled}
                      isResetting={isResetting}
                      isSubmitting={isSubmitting}
                    />
                  </div>
                </StyledModalActions>
              )}
            </Form>
          );
        }}
      </Formik>
    </CustomModal>
  );
};

export default observer(CreateEditFieldModal);
